import * as React from 'react';
import BPromise from 'bluebird';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { ColumnDefinition, getServerQueryFilter2, TABLE_SORT_DIRECTION } from 'Src/helpers/table/table';
import {
  getAllSuperAdminUserNotifications,
  getSuperAdminUserNotifications,
  getSuperAdminUserNotificationsCount,
  SuperAdminUserNotification
} from 'Src/helpers/service/superadmin/users';
import './SuperAdminUserNotifications.css';

const NOTIFICATION_TYPE_OPTIONS = [
  'PERMISSION_REQUEST_ACCEPTED',
  'PERMISSION_REQUEST_REJECTED',
  'PERMISSION_CREATED',
  'PERMISSION_REQUEST_CREATED',
  'MERGE_STUDENT',
  'MERGE_PARENT',
  'EVENT_CREATED',
  'EVENT_UPDATED',
  'EVENT_CANCELED',
  'EVENT_DETAILS_UPDATED',
  'EVENT_DETAILS_STAFF_ADDED',
  'EVENT_TEAM_PLAYER_ADDED',
  'EVENT_TEAM_PLAYER_UPDATED',
  'EVENT_TEAM_PLAYER_REMOVED',
  'EVENT_INDIVIDUAL_PLAYER_ADDED',
  'EVENT_INDIVIDUAL_PLAYER_REMOVED',
  'EVENT_TEAM_CHANGED',
  'MESSAGE_INBOX_CONSENT_REQUEST',
  'EVENT_AVAILABILITY_REQUESTED',
  'HIGH_LEVEL_TOURNAMENT_AVAILABILITY_REQUEST',
  'HIGH_LEVEL_TOURNAMENT_AVAILABILITY_DEADLINE',
  'HIGH_LEVEL_TOURNAMENT_AVAILABILITY_REMINDER',
  'HIGH_LEVEL_TOURNAMENT_AVAILABILITY_RESPONSE',
  'HIGH_LEVEL_TOURNAMENT_AVAILABILITY_OVERRIDE',
  'HIGH_LEVEL_TOURNAMENT_AVAILABILITY_CANCELLED',
  'EVENT_ELIGIBLE_STUDENTS',
  'EVENT_CUSTOM',
  'CLUB_CREATED',
  'CLUB_PARTICIPATION_INVITE',
  'CLUB_ACTIVATED',
  'CLUB_ACTIVATED_WAITING_LIST_NOTIFICATION',
  'CLUB_PROGRAMME_BOOKING_SUBMITTED',
  'CLUB_PROGRAMME_BOOKING_OPENED',
  'CLUB_PROGRAMME_BOOKING_OPENS_SOON',
  'CLUB_PROGRAMME_BOOKING_CLOSES_SOON',
  'CLUB_PROGRAMME_CLUBS_ACTIVATED',
  'EVENT_FIXTURE_REMINDER',
  'EVENT_CLUB_REMINDER',
  'INVITE_INCOMING',
  'INVITE_ACCEPTED_BY_OPPONENT',
  'INVITE_REJECTED_BY_OPPONENT',
  'NOTIFICATION_CHANNEL_TEST',
  'ABSENCE_REQUEST_SENT',
  'ABSENCE_REQUEST_UPDATED',
  'SCHOOL_TOURNAMENT_INVITE_CREATED',
  'SCHOOL_ADDED_TO_TOURNAMENT_PARTICIPANT',
  'SCHOOL_ADDED_TO_TOURNAMENT_WAITING_LIST',
  'SCHOOL_REMOVED_FROM_TOURNAMENT',
  'SCHOOL_REMOVED_FROM_TOURNAMENT_WAITING_LIST',
  'TOURNAMENT_PARTICIPANT_REMOVED_FROM_WAITING_LIST',
  'TOURNAMENT_CUSTOM',
  'TOURNAMENT_COMMUNICATION',
  'TOURNAMENT_REMINDER_TO_UNION',
  'TOURNAMENT_REMINDER_TO_SCHOOL',
  'TOURNAMENT_DETAILS_UPDATED',
  'CONFIRMATION_REQUEST_CREATED',
  'TOURNAMENT_CONFIRMATION_REQUEST_ACCEPTED',
  'TOURNAMENT_CONFIRMATION_REQUEST_REJECTED',
  'TOURNAMENT_CONFIRMATION_REQUEST_ACCEPTED_SCHOOL',
  'TOURNAMENT_CONFIRMATION_REQUEST_REJECTED_SCHOOL',
  'TOURNAMENT_PARTICIPANT_ADDED',
  'PARTICIPANT_ADDED_TO_TOURNAMENT_WAITING_LIST',
  'TOURNAMENT_PARTICIPANT_REMOVED',
  'GENERAL_MESSAGE',
  'CUSTOM',
  'ADMIN_PERMISSION_REQUEST_ACCEPTED',
  'PERMISSION_REQUEST_AUTO_ACCEPTED_USER',
  'ADMIN_NEW_USER_REGISTERED',
  'ADMIN_MODULE_SUBSCRIPTION_REQUESTED',
  'CLUB_PAYMENT_SUCCESSFUL',
  'REFUND_PROCESS_INITIATED',
  'WAITING_LIST_CAN_BOOK',
  'NEW_RECORD_ADDED',
  'AWARD_GRANTED',
  'EVENT_ATTENDANCE_ABSENT',
  'EVENT_ATTENDANCE_PRESENT',
  'EVENT_REPORT_ADDED',
  'EVENT_INDIVIDUAL_SCORE_ADDED',
  'EVENT_INDIVIDUAL_SCORE_UPDATED',
  'EVENT_TASK_ASSIGNED_ADDED',
  'EVENT_TASK_ASSIGNED_UPDATED'
];

const STATUS_OPTIONS = [
  { text: 'Enabled', value: 'ENABLED' },
  { text: 'Disabled', value: 'DISABLED' }
];

const DELIVERY_STATUS_OPTIONS = [
  'NONE',
  'NO_CHANNELS',
  'UNSUPPORTED_CHANNEL_TYPE',
  'SCHEDULED',
  'SENDING',
  'SENT',
  'DELIVERED',
  'READ',
  'ERROR',
  'NOT_SENT'
].map(status => ({ text: formatValue(status), value: status }));

const NOTIFICATION_OPTIONS: any = {
  type: NOTIFICATION_TYPE_OPTIONS.map(type => ({ text: formatValue(type), value: type })),
  status: STATUS_OPTIONS,
  deliveryStatus: DELIVERY_STATUS_OPTIONS
};

function getDefaultFilters(): any {
  return {
    type: [],
    title: '',
    body: '',
    status: [],
    deliveryStatus: [],
    createdAt: {
      from: '',
      to: ''
    },
    updatedAt: {
      from: '',
      to: ''
    }
  };
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatValue(value?: string): string {
  return value
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, letter => letter.toUpperCase())
    : '';
}

function getId(item: any): string {
  return item ? item.id || item._id || '' : '';
}

function getNotificationType(notification: SuperAdminUserNotification): string {
  return formatValue(notification.type);
}

function getNotificationTitle(notification: SuperAdminUserNotification): string {
  return (
    notification.title ||
    (notification.generatedMessages &&
      notification.generatedMessages.push &&
      notification.generatedMessages.push.title) ||
    (notification.generatedMessages &&
      notification.generatedMessages.email &&
      notification.generatedMessages.email.subject) ||
    ''
  );
}

function truncateText(value: string, maxLength: number): string {
  if (!value) {
    return '';
  }

  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}

function getNotificationBody(notification: SuperAdminUserNotification): string {
  const body =
    notification.body ||
    (notification.generatedMessages &&
      notification.generatedMessages.push &&
      notification.generatedMessages.push.body) ||
    (notification.generatedMessages &&
      notification.generatedMessages.email &&
      notification.generatedMessages.email.body) ||
    (notification.generatedMessages && notification.generatedMessages.sms && notification.generatedMessages.sms.body) ||
    '';

  return truncateText(body, 120);
}

function getNotificationStatus(notification: SuperAdminUserNotification): string {
  return formatValue(notification.status);
}

function getNotificationDeliveryStatus(notification: SuperAdminUserNotification): string {
  return formatValue(notification.deliveryStatus);
}

function getNotificationChannels(notification: SuperAdminUserNotification): string {
  const channelStatus = notification.channelStatus || [];

  if (channelStatus.length === 0) {
    return '';
  }

  const delivered = channelStatus.filter(channel => channel && channel.deliveryStatus === 'DELIVERED').length;
  const sent = channelStatus.filter(channel => channel && channel.deliveryStatus === 'SENT').length;
  const errors = channelStatus.filter(channel => channel && channel.deliveryStatus === 'ERROR').length;
  const total = channelStatus.length;
  const parts = [`${total} channel${total === 1 ? '' : 's'}`];

  if (delivered > 0) {
    parts.push(`${delivered} delivered`);
  }

  if (sent > 0) {
    parts.push(`${sent} sent`);
  }

  if (errors > 0) {
    parts.push(`${errors} error${errors === 1 ? '' : 's'}`);
  }

  return parts.join(' / ');
}

function getNotificationCreatedAt(notification: SuperAdminUserNotification): string {
  return formatDateTime(notification.createdAt);
}

function getNotificationUpdatedAt(notification: SuperAdminUserNotification): string {
  return formatDateTime(notification.updatedAt);
}

function getNotificationShortId(notification: SuperAdminUserNotification): string {
  const id = getId(notification);
  return id ? id.slice(-8) : '';
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Type',
    field: 'type',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getNotificationType
  },
  {
    text: 'Title',
    field: 'title',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getNotificationTitle
  },
  {
    text: 'Body',
    field: 'body',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getNotificationBody
  },
  {
    text: 'Status',
    field: 'status',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getNotificationStatus
  },
  {
    text: 'Delivery status',
    field: 'deliveryStatus',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getNotificationDeliveryStatus
  },
  {
    text: 'Channels',
    field: 'channelStatus',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getNotificationChannels
  },
  {
    text: 'Id',
    field: 'id',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getNotificationShortId
  },
  {
    text: 'Created',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getNotificationCreatedAt
  },
  {
    text: 'Updated',
    field: 'updatedAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getNotificationUpdatedAt
  }
];

interface Props {
  userId: string;
}

interface State {
  items: SuperAdminUserNotification[];
  selectedItems: SuperAdminUserNotification[];
  currentPage: number;
  total: number;
  filters: any;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  isSelectAllChecked: boolean;
  isLoading: boolean;
  errorMessage: string;
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  notificationToView?: SuperAdminUserNotification;
}

function parseDateFilterValue(value?: string): Date | undefined {
  if (!value) {
    return undefined;
  }

  const parts = value.split('-');

  if (parts.length !== 3) {
    return undefined;
  }

  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);

  if (!day || !month || !year) {
    return undefined;
  }

  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
}

function getDateIntervalWhere(value: any): any | undefined {
  if (!value || (!value.from && !value.to)) {
    return undefined;
  }

  const where: any = {};
  const from = parseDateFilterValue(value.from);
  const to = parseDateFilterValue(value.to);

  if (from) {
    where.$gte = from.toISOString();
  }

  if (to) {
    to.setUTCHours(23, 59, 59, 999);
    where.$lte = to.toISOString();
  }

  return Object.keys(where).length > 0 ? where : undefined;
}

function getNotificationsWhere(filters: any): any {
  const where: any = {};

  if (Array.isArray(filters.type) && filters.type.length > 0) {
    where.type = { $in: filters.type };
  }

  if (filters.title) {
    where.title = { like: filters.title, options: 'i' };
  }

  if (filters.body) {
    where.body = { like: filters.body, options: 'i' };
  }

  if (Array.isArray(filters.status) && filters.status.length > 0) {
    where.status = { $in: filters.status };
  }

  if (Array.isArray(filters.deliveryStatus) && filters.deliveryStatus.length > 0) {
    where.deliveryStatus = { $in: filters.deliveryStatus };
  }

  const createdAt = getDateIntervalWhere(filters.createdAt);
  const updatedAt = getDateIntervalWhere(filters.updatedAt);

  if (createdAt) {
    where.createdAt = createdAt;
  }

  if (updatedAt) {
    where.updatedAt = updatedAt;
  }

  return where;
}

function renderField(label: string, value?: React.ReactNode): React.ReactNode {
  if (typeof value === 'undefined' || value === null || value === '') {
    return null;
  }

  return (
    <div className="SuperAdminUserNotificationsDetailsRow" key={label}>
      <div className="SuperAdminUserNotificationsDetailsLabel">{label}</div>
      <div className="SuperAdminUserNotificationsDetailsValue">{value}</div>
    </div>
  );
}

function renderJsonBlock(title: string, value: any, isOpen?: boolean): React.ReactNode {
  if (typeof value === 'undefined' || value === null) {
    return null;
  }

  return (
    <details className="SuperAdminUserNotificationsDetailsJson" open={Boolean(isOpen)}>
      <summary>{title}</summary>
      <pre className="SuperAdminUserNotificationsJsonBlock">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

function getChannelType(channel: any): string {
  return channel.channelType || channel.type || channel.notificationChannelType || '';
}

function getChannelDeliveryStatus(channel: any): string {
  return channel.deliveryStatus || channel.status || '';
}

function getChannelTitle(channel: any): string {
  const type = getChannelType(channel);
  const id = channel.channelId || channel.notificationChannelId || channel.id || channel._id || '';
  const typeText = formatValue(type);

  return [typeText, id].filter(Boolean).join(' / ');
}

export class SuperAdminUserNotifications extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      selectedItems: [],
      currentPage: FIRST_PAGE,
      total: 0,
      filters: getDefaultFilters(),
      isShowFilter: false,
      isDataFiltered: false,
      isSelectAllChecked: false,
      isLoading: false,
      errorMessage: '',
      sortDirection: '',
      sortColumnsName: ''
    };
  }

  componentDidMount() {
    this.setItems(FIRST_PAGE);
  }

  setItems = (page: number): void => {
    const { userId } = this.props;
    const { filters, sortDirection, sortColumnsName } = this.state;
    const where = getNotificationsWhere(filters);
    const order = sortDirection && sortColumnsName ? `${sortColumnsName} ${sortDirection}` : 'createdAt DESC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    this.setState({ isLoading: true, errorMessage: '' });

    BPromise.all([
      getSuperAdminUserNotificationsCount(userId, where),
      getSuperAdminUserNotifications(userId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          items,
          total: countObj.count,
          currentPage: page,
          isLoading: false,
          errorMessage: '',
          isDataFiltered: Object.keys(where).length > 0,
          selectedItems: [],
          isSelectAllChecked: false
        });
      })
      .catch(error => {
        console.error('Can not load superadmin user notifications', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load notifications. Please check the session and try again.'
        });
      });
  };

  setCurrentPageParams = (currentPage: number): void => {
    this.setItems(currentPage);
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(column => column.field === filterField);
      const filterType = filter && filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const selectOptions = event.target.options;
        const value = [];

        for (let i = 0; i < selectOptions.length; i++) {
          if (selectOptions[i].selected) {
            value.push(selectOptions[i].value);
          }
        }

        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    this.setItems(FIRST_PAGE);
  };

  onClearFilterClick = (): void => {
    this.setState({ filters: getDefaultFilters() }, () => this.setItems(FIRST_PAGE));
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName } = this.state;
    const nextSortDirection = sortColumnsName === sortField && sortDirection === 'ASC' ? 'DESC' : 'ASC';

    this.setState(
      {
        sortColumnsName: sortField,
        sortDirection: nextSortDirection as TABLE_SORT_DIRECTION
      },
      () => this.setItems(FIRST_PAGE)
    );
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemId = getId(selectedItem);
    const selectedItemIndex = selectedItems.findIndex(item => getId(item) === selectedItemId);

    if (selectedItemIndex === -1) {
      this.setState({ selectedItems: [selectedItem] });
    } else {
      this.setState({ selectedItems: [] });
    }
  };

  onSelectAllClick = (): void => {
    const where = getNotificationsWhere(this.state.filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminUserNotifications(this.props.userId, where)
      .then(items => {
        this.setState({
          selectedItems: items,
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin user notifications', error);
        this.setState({ isLoading: false, errorMessage: 'Can not select all notifications.' });
      });
  };

  onSelectAllOnPageClick = (): void => {
    this.setState({ selectedItems: [...this.state.items], isSelectAllChecked: false });
  };

  onUnselectAllClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });
  };

  onViewDetailsClick = (): void => {
    const notificationToView = this.state.selectedItems[0];
    this.setState({ notificationToView });
  };

  closeDetails = (): void => {
    this.setState({ notificationToView: undefined });
  };

  renderGeneratedMessages(notification: SuperAdminUserNotification): React.ReactNode {
    const generatedMessages = notification.generatedMessages || {};
    const email = generatedMessages.email;
    const push = generatedMessages.push;
    const sms = generatedMessages.sms;

    if (!email && !push && !sms) {
      return <div className="text-muted">No generated messages saved for this notification.</div>;
    }

    return (
      <div className="SuperAdminUserNotificationsMessageGrid">
        {email && (
          <div className="SuperAdminUserNotificationsMessageCard">
            <div className="SuperAdminUserNotificationsMessageTitle">Email</div>
            {renderField('Subject', email.subject)}
            {renderField('Body', <pre className="SuperAdminUserNotificationsMessageBody">{email.body || ''}</pre>)}
          </div>
        )}
        {push && (
          <div className="SuperAdminUserNotificationsMessageCard">
            <div className="SuperAdminUserNotificationsMessageTitle">Push</div>
            {renderField('Title', push.title)}
            {renderField('Body', <pre className="SuperAdminUserNotificationsMessageBody">{push.body || ''}</pre>)}
          </div>
        )}
        {sms && (
          <div className="SuperAdminUserNotificationsMessageCard">
            <div className="SuperAdminUserNotificationsMessageTitle">SMS</div>
            {renderField('Body', <pre className="SuperAdminUserNotificationsMessageBody">{sms.body || ''}</pre>)}
          </div>
        )}
      </div>
    );
  }

  renderChannelStatus(notification: SuperAdminUserNotification): React.ReactNode {
    const channelStatus = notification.channelStatus || [];

    if (channelStatus.length === 0) {
      return <div className="text-muted">No channel status entries.</div>;
    }

    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered SuperAdminUserNotificationsChannelTable">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Delivery</th>
              <th>Updated</th>
              <th>Error / details</th>
            </tr>
          </thead>
          <tbody>
            {channelStatus.map((channel, index) => {
              const errorText =
                channel.error || channel.errorMessage || channel.message || channel.reason || channel.details || '';

              return (
                <tr key={`notification_channel_${index}`}>
                  <td>{getChannelTitle(channel)}</td>
                  <td>{formatValue(getChannelDeliveryStatus(channel))}</td>
                  <td>{formatDateTime(channel.updatedAt || channel.createdAt)}</td>
                  <td>
                    {typeof errorText === 'string' ? errorText : ''}
                    {typeof errorText !== 'string' && renderJsonBlock('Details', errorText)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderDetailsModal() {
    const { notificationToView } = this.state;

    if (!notificationToView) {
      return null;
    }

    return (
      <>
        <div className="SuperAdminUserNotificationsModalBackdrop" onClick={this.closeDetails} />
        <div className="SuperAdminUserNotificationsModal">
          <div className="SuperAdminUserNotificationsModalHeader">
            <div>
              <h5 className="mb-1">Notification details</h5>
              <div className="text-muted small">{getId(notificationToView)}</div>
            </div>
            <button type="button" className="close" onClick={this.closeDetails}>
              &times;
            </button>
          </div>
          <div className="SuperAdminUserNotificationsModalBody">
            <div className="SuperAdminUserNotificationsDetailsGrid mb-3">
              {renderField('Type', formatValue(notificationToView.type))}
              {renderField('Status', formatValue(notificationToView.status))}
              {renderField('Delivery status', formatValue(notificationToView.deliveryStatus))}
              {renderField('Title', getNotificationTitle(notificationToView))}
              {renderField('Created', formatDateTime(notificationToView.createdAt))}
              {renderField('Updated', formatDateTime(notificationToView.updatedAt))}
              {renderField('Fact ID', notificationToView.factId)}
              {renderField('Action descriptor ID', notificationToView.actionDescriptorId)}
              {renderField('Entity type', notificationToView.entityType)}
              {renderField('Additional description', notificationToView.additionalDescription)}
            </div>

            <h6>Generated messages</h6>
            {this.renderGeneratedMessages(notificationToView)}

            <h6 className="mt-4">Channel status</h6>
            {this.renderChannelStatus(notificationToView)}

            <div className="mt-4">
              {renderJsonBlock('Details JSON', notificationToView.details, true)}
              {renderJsonBlock('Creator details JSON', notificationToView.creatorDetails)}
              {renderJsonBlock('Full notification JSON', notificationToView)}
            </div>
          </div>
        </div>
      </>
    );
  }

  render() {
    const {
      items,
      selectedItems,
      currentPage,
      total,
      filters,
      isShowFilter,
      isDataFiltered,
      isSelectAllChecked,
      isLoading,
      errorMessage,
      sortDirection,
      sortColumnsName
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'View details',
        onItemClick: this.onViewDetailsClick,
        isActive: selectedItems.length === 1
      }
    ];

    return (
      <div className="SuperAdminUserNotifications">
        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {isLoading && <Loader />}

        {!isLoading && (
          <Grid2
            dataItems={items}
            filters={filters}
            options={NOTIFICATION_OPTIONS}
            currentPage={currentPage}
            total={total}
            isSelectAllChecked={isSelectAllChecked}
            isDataFiltered={isDataFiltered}
            sortDirection={sortDirection}
            sortColumnsName={sortColumnsName}
            isShowFilter={isShowFilter}
            dataItemsSelected={selectedItems}
            columns={COLUMNS}
            actionItems={actionItems}
            onItemClick={this.onItemClick}
            onSortClick={this.onTableSortClick}
            onApplyFilterClick={this.onApplyFilterClick}
            onClearFilterClick={this.onClearFilterClick}
            onTableFilterChange={this.onTableFilterChange}
            onTableFilterClick={this.onTableFilterClick}
            setCurrentPageParams={this.setCurrentPageParams}
            onSelectAllClick={this.onSelectAllClick}
            onSelectAllOnPageClick={this.onSelectAllOnPageClick}
            onUnselectAllClick={this.onUnselectAllClick}
          />
        )}

        {this.renderDetailsModal()}
      </div>
    );
  }
}
